<template>
    <div v-if="album" class="flex flex-col h-screen-header">
        <div ref="heroContainer" class="flex-none rounded-xl transition-all duration-300 ease-out"
             :class="{'hero-minimized': isHeroMinimized}">
            <Hero :image="album.image" :is-minimized="isHeroMinimized">
                <!-- 普通状态的内容 -->
                <div v-if="!isHeroMinimized" class="flex flex-col justify-between h-[7rem] md:h-56"
                     style="transition: opacity 0.2s ease-out">
                    <div class="ml-4 flex flex-col justify-start items-center md:items-start z-10">
                        <div class="flex items-center ml-1">
                            <div class="text-4xl font-medium">{{ album.name }}</div>
                            <button class="btn btn-active btn-sm ml-2" @click.stop="addFavouriteAlbum">
                                <svg xmlns="http://www.w3.org/2000/svg" :class="['h-6', 'w-6', isFavourite ? 'fill-current' : '']" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
                            </button>
                        </div>
                        <div class="flex flex-col items-center md:items-start my-2 text-gray-600 !hidden md:!block">
                            <span>{{ $tt('专辑 由 {artist} · {year} 发行', { artist: album.artist, year: album.year }) }}</span>
                            <span>{{ $tt('{trackCount} 首歌曲, {duration} 时长 添加于{createdAt} · {genre}', { trackCount: album.tracks.length, duration: formatDuration(album.duration), createdAt: formatDate(album.created), genre: album.genreId }) }}</span>
                        </div>
                        <div class="flex flex-col items-center md:items-start my-1 text-gray-600 !hidden md:!block" v-if="album.comment">
                            <span class="text-sm cursor-pointer hover:text-primary/80 line-clamp-3" onclick="album_comment.showModal()" :title="album.comment">
                                {{ album.comment.length > 150 ? album.comment.substring(0, 150) + '...' : album.comment }}
                            </span>
                        </div>
                        <div class="!hidden md:!block">
                            <div class="badge badge-neutral mr-1">
                                <router-link :to="{ name: 'artist', params: { id: album.artistId } }">
                                    {{ album.artist }}
                                </router-link>
                            </div>
                            <div class="badge badge-neutral mr-1" v-if="album.year">
                                {{ album.year }}
                            </div>
                            <div class="badge badge-neutral mr-1" v-if="album.genreId">
                                <router-link :to="{ name: 'genre', params: { id: album.genreId } }">
                                    {{ album.genreId }}
                                </router-link>
                            </div>
                            <div v-for="(item, index) in displayedOtherArtists" :key="index" class="badge badge-neutral mr-1">
                                {{ item }}
                            </div>
                            <div v-if="hasMoreArtists" class="badge badge-outline mr-1 cursor-pointer hover:badge-neutral"
                                 @click="toggleShowAllArtists"
                                 :title="showAllArtists ? $tt('收起') : $tt('还有 {count} 个艺术家', { count: remainingArtistsCount })">
                                {{ showAllArtists ? $tt('收起') : `+${remainingArtistsCount}` }}
                            </div>
                        </div>
                    </div>
                    <div class="ml-4 z-10 flex flex-row justify-center md:justify-start">
                        <button class="btn btn-neutral btn-active btn-sm" @click="playNow(album.id)">
                            PLAY
                        </button>
                        <label for="my-drawer-album" class="btn btn-neutral btn-active btn-sm ml-2" @click="handleEditAlbum" v-if="isSuperuser">EDIT</label>
                        <button class="btn btn-neutral btn-active btn-sm ml-2" onclick="album_delete.showModal()" v-if="isSuperuser">
                            DELETE
                        </button>
                    </div>
                </div>

                <!-- 缩小状态的内容 -->
                <div v-show="isHeroMinimized" class="flex items-center justify-between w-full py-2 px-1"
                     style="transition: opacity 0.2s ease-out">
                    <div class="flex flex-col">
                        <div class="text-lg font-medium truncate max-w-[200px]">{{ album.name }}</div>
                        <div class="text-sm text-gray-600 dark:text-gray-300 truncate max-w-[200px]">{{ album.artist }} · {{ album.year }}</div>
                    </div>
                    <div class="flex items-center space-x-2">
                        <button class="btn btn-active btn-xs" @click.stop="addFavouriteAlbum">
                            <svg xmlns="http://www.w3.org/2000/svg" :class="['h-4', 'w-4', isFavourite ? 'fill-current' : '']" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                            </svg>
                        </button>
                        <button class="btn btn-neutral btn-xs" @click="playNow(album.id)">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z"/>
                            </svg>
                        </button>
                    </div>
                </div>
            </Hero>
        </div>
        <div ref="trackContainer" class="flex-1 mt-2 overflow-y-auto rounded-xl" @scroll="handleScroll">
            <TrackList :tracks="album.tracks" no-album />
        </div>

        <!-- 编辑专辑信息抽屉 - 移到最外层避免 z-index 问题 -->
        <div class="drawer drawer-end" v-if="isSuperuser">
            <input id="my-drawer-album" type="checkbox" class="drawer-toggle" v-model="isEditAlbum" />
            <div class="drawer-side" style="z-index: 9999;">
                <label for="my-drawer-album" aria-label="close sidebar" class="drawer-overlay"></label>
                <div class="p-4 w-80 min-h-full bg-base-200 text-base-content">
                    <div class="font-medium text-xl mb-4">{{ $tt('编辑专辑信息') }}</div>

                    <div class="mb-1">{{ $tt('专辑:') }}</div>
                    <div>
                        <input type="text"
                            v-model="FormData.name"
                            class="input input-bordered input-sm w-full max-w-xs" />
                    </div>
                    <div class="mb-1 mt-1">{{ $tt('专辑封面:') }}</div>
                    <div v-if="reloadImg">
                        <!-- 图片上传 -->
                        <div class="relative">
                            <!-- 图片预览区域 -->
                            <div class="border-2 border-dashed border-gray-300 rounded-lg p-2 text-center hover:border-gray-400 transition-colors">
                                <div v-if="albumImagePreview" class="relative inline-block">
                                    <img :src="albumImagePreview" :alt="$tt('专辑封面')" class="max-w-full max-h-48 rounded-lg shadow-md">
                                    <button @click.stop="removeAlbumImage"
                                        class="absolute -top-2 -right-2 btn btn-circle btn-xs btn-error text-white hover:bg-red-600 z-50"
                                        type="button" style="pointer-events: auto;">
                                        ✕
                                    </button>
                                    <!-- 替换图片按钮 -->
                                    <button @click.stop="triggerAlbumImageReplacement"
                                        class="absolute -bottom-2 -right-2 btn btn-circle btn-xs btn-neutral text-white z-50"
                                        type="button" style="pointer-events: auto;" :title="$tt('替换图片')">
                                        +
                                    </button>
                                </div>
                                <div v-else>
                                    <svg class="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                                        <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                            stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                    </svg>
                                    <p class="mt-2 text-sm text-gray-500">{{ $tt('点击上传或拖拽图片到此处') }}</p>
                                    <p class="text-xs text-gray-400">{{ $tt('支持 JPG, PNG, GIF (最大 5MB)') }}</p>
                                </div>

                                <!-- 隐藏的文件输入 -->
                                <input ref="albumImageInput" type="file" accept="image/*"
                                    @change="handleAlbumImageChange"
                                    class="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                    :disabled="isUploadingAlbumImage" v-show="!albumImagePreview">
                                <!-- 上传进度 -->
                                <div v-if="isUploadingAlbumImage"
                                    class="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-lg">
                                    <div class="text-center">
                                        <span class="loading loading-spinner loading-md"></span>
                                        <p class="mt-2 text-sm text-gray-600">{{ $tt('上传中...') }}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="mb-1 mt-1">{{ $tt('描述:') }}</div>
                    <textarea class="textarea textarea-bordered textarea-xs w-full max-w-xs h-48" placeholder="." v-model="FormData.comment"></textarea>
                    <button class="btn btn-neutral btn-sm btn-wide" @click="handleSave">{{ $tt('保存') }}</button>
                    <button class="btn btn-sm btn-wide btn-primary mt-2" @click="handleSaveMetadata">{{ $tt('保存并更新音乐元数据') }}</button>
                </div>
            </div>
        </div>

        <dialog id="album_delete" class="modal modal-bottom sm:modal-middle">
            <div class="modal-box">
                <h3 class="text-lg font-bold">{{ $tt('删除专辑') }}</h3>
                <p class="py-4">{{ $tt('从音乐收藏中删除此专辑，但不会删除源文件。') }}</p>
                <div class="modal-action">
                    <form method="dialog">
                        <!-- if there is a button in form, it will close the modal -->
                        <button class="btn btn-neutral btn-sm mr-2" @click="handleDelete">{{ $tt('确认') }}</button>
                        <button class="btn btn-sm">{{ $tt('取消') }}</button>
                    </form>
                </div>
            </div>
        </dialog>
        <dialog id="album_comment" class="modal">
            <div class="modal-box">
                <h3 class="text-lg font-bold">{{ $tt('专辑简介:') }}{{album.name}}</h3>
                <div class="py-4">
                    <textarea class="textarea textarea-bordered textarea-xs w-full h-96" placeholder="." v-model="album.comment"></textarea>
                </div>
            </div>
            <form method="dialog" class="modal-backdrop">
                <button>close</button>
            </form>
        </dialog>
    </div>
</template>
<script>
    import Hero from '../../../components/library/Hero'
    import TrackList from '@/views/library/track/TrackList'
    import {mapGetters} from 'vuex'

    export default {
        components: {
            Hero, TrackList
        },
                    data() {
                return {
                    album: null,
                    id: this.$route.params.id,
                    isFavourite: false,
                    FormData: {},
                    artistsData: [],
                    uploadUrl: '/apimt/upload_image/',
                    isEditAlbum: false,
                    // 新增的图片上传相关数据
                    albumImagePreview: null,
                    isUploadingAlbumImage: false,
                    reloadImg: true,
                    // 滑动动画相关数据
                    isHeroMinimized: false,
                    scrollThreshold: 50, // 滑动阈值
                    scrollHysteresis: 20, // 防抖区间（滞后值），增加到20px
                    isMobile: false,
                    scrollAnimationFrame: null,
                    lastScrollTop: 0, // 记录上次滚动位置
                    // 艺术家显示相关数据
                    showAllArtists: false,
                    maxDisplayedArtists: 3
                }
            },
        computed: {
            ...mapGetters({
                isSuperuser: 'getIsSuperuser'
            }),
            // 显示的其他艺术家列表
            displayedOtherArtists() {
                if (!this.album?.otherArtist || this.album.otherArtist.length === 0) {
                    return []
                }
                
                if (this.showAllArtists || this.album.otherArtist.length <= this.maxDisplayedArtists) {
                    return this.album.otherArtist
                }
                
                return this.album.otherArtist.slice(0, this.maxDisplayedArtists)
            },
            
            // 是否有更多艺术家
            hasMoreArtists() {
                return this.album?.otherArtist && this.album.otherArtist.length > this.maxDisplayedArtists
            },
            
            // 剩余艺术家数量
            remainingArtistsCount() {
                if (!this.album?.otherArtist) return 0
                return this.album.otherArtist.length - this.maxDisplayedArtists
            }
        },
        async created() {
            this.album = await this.$api.subsonic.getAlbumDetails(this.id)
            this.isFavourite = this.album.favourite
        },
        mounted() {
            this.checkMobile()
            window.addEventListener('resize', this.checkMobile)
        },
        beforeDestroy() {
            window.removeEventListener('resize', this.checkMobile)
            if (this.scrollAnimationFrame) {
                cancelAnimationFrame(this.scrollAnimationFrame)
            }
        },
        methods: {
            async playNow(id) {
                const album = await this.$api.subsonic.getAlbumDetails(id)
                return this.$store.dispatch('playTrackList', {
                    tracks: album.tracks
                })
            },
            shuffleNow() {
                return this.$store.dispatch('player/shuffleNow', {
                    tracks: this.album && this.album.tracks ? this.album.tracks : []
                })
            },
            setNextInQueue() {
                if (this.album) {
                    return this.$store.dispatch('player/setNextInQueue', this.album.tracks)
                }
            },
            addToQueue() {
                if (this.album) {
                    return this.$store.dispatch('player/addToQueue', this.album.tracks)
                }
            },
            toggleFavourite() {
                return this.favouriteStore.toggle('album', this.id)
            },
            formatDuration(value) {
                if (!isFinite(value)) {
                    return '∞'
                }
                const minutes = Math.floor(value / 60)
                const seconds = Math.floor(value % 60)
                return (minutes < 10 ? '0' : '') + minutes + ':' + (seconds < 10 ? '0' : '') + seconds
            },
            addFavouriteAlbum() {
                if (this.isFavourite) {
                    this.$api.subsonic.removeFavourite(this.album.id, 'album').then((res) => {
                        this.isFavourite = false
                    })
                } else {
                    this.$api.subsonic.addFavourite(this.album.id, 'album').then((res) => {
                        this.isFavourite = true
                    })
                }
            },
            handleSave() {
                this.$api.Task.postAlbum({
                    id: this.id,
                    album_img: this.FormData.album_img,
                    comment: this.FormData.comment,
                    name: this.FormData.name
                }).then((res) => {
                    if (res.result) {
                        this.$myMsg.notify({
                            title: this.$tt('修改成功！'),
                            type: 'success',
                            time: 2000,
                        })
                        this.$api.subsonic.getAlbumDetails(this.id).then((res) => {
                            this.album = res
                        })
                    } else {
                        this.$myMsg.notify({
                            title: this.$tt('创建失败！'),
                            type: 'error',
                            time: 5000,
                        })
                    }
                })
            },
            handleSaveMetadata() {
                this.$api.Task.postAlbum({
                    id: this.id,
                    album_img: this.FormData.album_img,
                    comment: this.FormData.comment,
                    name: this.FormData.name,
                    write_metadata: true
                }).then((res) => {
                    if (res.result) {
                        this.$myMsg.notify({
                            title: this.$tt('修改成功！'),
                            type: 'success',
                            time: 2000,
                        })
                        this.$api.subsonic.getAlbumDetails(this.id).then((res) => {
                            this.album = res
                        })
                        this.isEditAlbum = false
                    } else {
                        this.$myMsg.notify({
                            title: this.$tt('创建失败！'),
                            type: 'error',
                            time: 5000,
                        })
                    }
                })
            },
            handleDelete() {
                this.$api.Task.deleteAlbum({ 'id': this.id }).then((res) => {
                    if (res.result) {
                        this.$myMsg.notify({
                            title: this.$tt('删除成功！'),
                            type: 'success',
                            time: 2000,
                        })
                        this.$router.push({path: '/albums/recently-added/'})
                    } else {
                        this.$myMsg.notify({
                            title: this.$tt('删除失败！'),
                            type: 'error',
                            time: 2000,
                        })
                    }
                })
            },
            handleEditAlbum() {
                if (this.album.image) {
                    this.albumImagePreview = this.album.image
                }
                this.FormData = {
                    name: this.album.name,
                    comment: this.album.comment
                }
            },
            // 渲染图片
            handleRes(response) {
                if (response.result) {
                    this.FormData.album_img = response.data
                    return true
                } else {
                    return false
                }
            },
            // 移除专辑封面
            removeAlbumImage() {
                this.albumImagePreview = null
                this.FormData.album_img = ''
                if (this.$refs.albumImageInput) {
                    this.$refs.albumImageInput.value = ''
                }
            },
            // 触发专辑封面替换
            triggerAlbumImageReplacement() {
                // 直接创建临时input，更可靠
                this.createTempFileInput('album')
            },
            
            // 创建临时文件输入
            createTempFileInput(type) {
                const input = document.createElement('input')
                input.type = 'file'
                input.accept = 'image/*'
                input.style.display = 'none'
                input.onchange = (event) => {
                    if (type === 'album') {
                        this.handleAlbumImageChange(event)
                    }
                    // 清理临时元素
                    if (input.parentNode) {
                        input.parentNode.removeChild(input)
                    }
                }
                document.body.appendChild(input)
                input.click()
            },
            // 处理专辑封面图片选择
            handleAlbumImageChange(event) {
                const file = event.target.files[0]
                if (!file) return
                
                // 验证文件类型
                if (!file.type.startsWith('image/')) {
                    this.$myMsg.notify({
                        title: this.$tt('文件类型错误'),
                        content: this.$tt('请选择图片文件'),
                        type: 'error',
                        time: 2000,
                    })
                    return
                }
                
                // 验证文件大小 (5MB)
                if (file.size > 5 * 1024 * 1024) {
                    this.$myMsg.notify({
                        title: this.$tt('文件过大'),
                        content: this.$tt('图片大小不能超过5MB'),
                        type: 'error',
                        time: 2000,
                    })
                    return
                }
                
                // 显示预览
                const reader = new FileReader()
                reader.onload = (e) => {
                    this.albumImagePreview = e.target.result
                }
                reader.readAsDataURL(file)
                
                // 上传图片
                this.uploadImage(file, 'album')
            },
            // 通用图片上传方法
            uploadImage(file, type) {
                const formData = new FormData()
                formData.append('upload_file', file)
                
                // 设置上传状态
                if (type === 'album') {
                    this.isUploadingAlbumImage = true
                }
                
                // 创建 XMLHttpRequest
                const xhr = new XMLHttpRequest()
                
                // 设置请求头
                xhr.open('POST', this.uploadUrl, true)
                xhr.setRequestHeader('X-CSRFToken', this.getCookie('django_vue_cli_csrftoken'))
                xhr.setRequestHeader('AUTHORIZATION', this.getCookie('AUTHORIZATION'))
                xhr.withCredentials = true
                
                // 处理响应
                xhr.onload = () => {
                    // 重置上传状态
                    if (type === 'album') {
                        this.isUploadingAlbumImage = false
                    }
                    
                    if (xhr.status === 200) {
                        try {
                            const response = JSON.parse(xhr.responseText)
                            if (this.handleRes(response)) {
                                this.$myMsg.notify({
                                    title: this.$tt('上传成功'),
                                    type: 'success',
                                    time: 2000,
                                })
                            } else {
                                    this.$myMsg.notify({
                                        title: this.$tt('上传失败'),
                                        content: response.message || this.$tt('未知错误'),
                                        type: 'error',
                                        time: 2000,
                                    })
                            }
                        } catch (e) {
                            console.log(e)
                            this.$myMsg.notify({
                                title: this.$tt('上传失败'),
                                content: this.$tt('服务器响应格式错误'),
                                type: 'error',
                                time: 2000,
                            })
                        }
                    } else {
                        this.$myMsg.notify({
                            title: this.$tt('上传失败'),
                            content: this.$tt('HTTP错误: {status}', { status: xhr.status }),
                            type: 'error',
                            time: 2000,
                        })
                    }
                }
                
                // 处理错误
                xhr.onerror = () => {
                    if (type === 'album') {
                        this.isUploadingAlbumImage = false
                    }
                    this.$myMsg.notify({
                        title: this.$tt('上传失败'),
                        content: this.$tt('网络错误'),
                        type: 'error',
                        time: 2000,
                    })
                }
                
                // 发送请求
                xhr.send(formData)
            },
            handleCopy(k, v) {
                if (k === 'album_img') {
                    this.albumImagePreview = v
                    this.FormData.album_img = v
                } else {
                    this.FormData[k] = v
                }
            },
            // 处理滑动事件 (使用 requestAnimationFrame 优化性能，添加滞后防抖)
            handleScroll(event) {
                if (this.scrollAnimationFrame) {
                    cancelAnimationFrame(this.scrollAnimationFrame)
                }

                this.scrollAnimationFrame = requestAnimationFrame(() => {
                    const scrollTop = event.target.scrollTop
                    const scrollDirection = scrollTop > this.lastScrollTop ? 'down' : 'up'
                    this.lastScrollTop = scrollTop

                    // 使用更大的滞后区间防止闪烁
                    // 向下滚动：超过阈值+滞后值才缩小
                    // 向上滚动：低于阈值-滞后值才恢复
                    if (!this.isHeroMinimized && scrollTop > this.scrollThreshold + this.scrollHysteresis) {
                        this.isHeroMinimized = true
                    } else if (this.isHeroMinimized && scrollTop < this.scrollThreshold - this.scrollHysteresis) {
                        this.isHeroMinimized = false
                    }
                    // 在临界区间内不做任何改变，保持当前状态
                })
            },
            // 检查是否为移动端
            checkMobile() {
                this.isMobile = window.innerWidth < 768 // md断点
            },
            
            // 切换显示所有艺术家的状态
            toggleShowAllArtists() {
                this.showAllArtists = !this.showAllArtists
            }
        }
    }
</script>

<style scoped>
/* 文本截断样式 */
.line-clamp-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.4;
    max-height: calc(1.4em * 3);
}

/* 移动端专辑封面缩小动画 */
.hero-minimized {
    transform: none;
    height: auto !important;
    min-height: auto !important;
}

/* Hero容器的平滑过渡 */
.rounded-xl {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    will-change: transform, height;
}

/* 防止内容切换时的闪烁 */
.hero-minimized > * {
    transition: opacity 0.2s ease-out;
}

/* 缩小状态的特殊样式 */
@media (max-width: 767px) {
    .hero-minimized {
        margin-bottom: 0.5rem;
    }
}

@media (min-width: 768px) {
    .hero-minimized {
        transform: none;
    }
}

</style>
